import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import type { PortfolioZoneReportDto } from '@/api/portfolio';
import type { WeeklyReportSections, WeeklySection } from '@/api/weeklyReports';

// Per-zone WEEK mini card for the «Все филиалы» report view: one human FACT
// line per configured weekly section, in the full week report's own units
// (owner feedback 2026-07-19: «столы −100%» tells nothing — say WHAT is
// measured, and never paint a green dot over missing data):
//   Выкладка → «полки заполнены на N%»      (avg shelf fill %, higher better)
//   Очередь  → «в очереди в среднем N чел»   (avg people in queue, lower better)
//   Столы    → «грязных столов ~N в день»    (validated dirty-table alerts/day,
//                                             lower better)
//   Задачи   → «задачи выполнены на N%»      (checklist completion %, higher
//                                             better)
// (metric per section = BE weekly_report/celery.py; wording = the
// weeklyReport.*Desc vocabulary the full report family uses.)
// Worst section first; click anywhere → drill into the zone's week report.
//
// Light semantics are inherited from WeekReport, not invented here:
// - `delta_pct` is the ABSOLUTE this_week − prev_week difference (BE `_delta`),
//   shown as-is in the section's unit — exactly like WeekReport's WeekDelta
//   («−2 мин vs пр. неделя»), never as a relative percent.
// - Coloring keeps the original weekHealth thresholds: worseFrac =
//   worse / |prev_week|, ≥15% → amber, ≥30% (2×) → red.
// - LOW DATA IS HONEST: this_week missing → grey dot + «мало данных за
//   неделю»; prev_week missing or below the section's floor (a base too small
//   for a meaningful comparison — the «−100%» garbage source) → grey dot,
//   current value only, «сравнить не с чем». Never a delta, never green.

type SectionKey = keyof WeeklyReportSections;
type Light = 'green' | 'amber' | 'red';

const SECTION_ORDER: SectionKey[] = ['layout', 'queue', 'tables', 'tasks'];

// Reuse the stored weekly-report section labels — same customer copy
// («Выкладка», «Очередь», «Столы», «Задачи») as the full week view family.
const SECTION_LABEL_KEY: Record<SectionKey, string> = {
  layout: 'weeklyReport.layout',
  queue: 'weeklyReport.queue',
  tables: 'weeklyReport.tables',
  tasks: 'weeklyReport.tasks',
};

const FACT_KEY: Record<SectionKey, string> = {
  layout: 'portfolio.weekFactLayout',
  queue: 'portfolio.weekFactQueue',
  tables: 'portfolio.weekFactTables',
  tasks: 'portfolio.weekFactTasks',
};

// Minimum |prev_week| (in the section's own unit) for a week-over-week
// comparison to mean anything. Below it, relative fractions explode
// (0.1 → 0.3 dirty tables/day is NOT «+200%») — the section goes grey instead.
const PREV_FLOOR: Record<SectionKey, number> = {
  layout: 5, // % shelf fill
  queue: 0.5, // avg people in queue
  tables: 1, // dirty tables per day
  tasks: 5, // % tasks completed
};

const AMBER_FRAC = 0.15; // weekHealth's original worsening threshold
const RED_FRAC = 0.3; // "clearly worse" = 2× the amber threshold

const LIGHT_DOT: Record<Light, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const LIGHT_TEXT: Record<Light, string> = {
  green: 'text-emerald-600 dark:text-emerald-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-500 dark:text-red-400',
};

const LIGHT_RING: Record<Light, string> = {
  green: 'ring-emerald-500/20',
  amber: 'ring-amber-500/30',
  red: 'ring-red-500/30',
};

// Grey/muted low-data dot — deliberately NOT part of the Light scale so the
// AllBranchesReport ranking contract (light ∈ green|amber|red) stays intact.
const MUTED_DOT = 'bg-muted-foreground/40';

/** How much worse the week got, relative to the previous week's level.
 *  Positive = worse, negative = better, 0 = flat. Callers guarantee prev is
 *  above the section floor. */
function worseFrac(delta: number, prev: number, higherIsBetter: boolean): number {
  const worse = higherIsBetter ? -delta : delta;
  return worse / Math.abs(prev);
}

function lightOf(frac: number): Light {
  if (frac >= RED_FRAC) return 'red';
  if (frac >= AMBER_FRAC) return 'amber';
  return 'green';
}

// Absolute dirty-table severity for when there's NO comparable previous week
// (prev below PREV_FLOOR). The week-over-week delta is noise on a thin base
// (0.1→0.3 dirty tables isn't «+200%»), but a branch that went from clean to
// dirty must still COLOR instead of reading «сравнить не с чем» (797/798).
// Metric = validated dirty-table alerts/day (lower is better). Amber cutoff =
// PREV_FLOOR.tables — the rate we already treat as "meaningful" — so at/above
// it a branch colors; ~3×/day is clearly bad. Thresholds tunable.
const TABLES_ABS_RED = 3;
function absTablesLight(alertsPerDay: number): Light {
  if (alertsPerDay >= TABLES_ABS_RED) return 'red';
  if (alertsPerDay >= PREV_FLOOR.tables) return 'amber';
  return 'green';
}

/** Percent-typed sections show whole numbers; count-typed keep one decimal
 *  under 10 («1.2 чел», «0.8 в день»), whole above. */
function fmtValue(key: SectionKey, v: number): string {
  if (key === 'layout' || key === 'tasks') return String(Math.round(v));
  return String(Math.abs(v) >= 10 ? Math.round(v) : Math.round(v * 10) / 10);
}

/** Signed ABSOLUTE delta in the section's unit («−4%», «+0.3»), WeekReport's
 *  WeekDelta style. Null when it rounds to nothing (flat week). */
function fmtDelta(key: SectionKey, delta: number): string | null {
  const text = fmtValue(key, Math.abs(delta));
  if (text === '0') return null;
  const sign = delta > 0 ? '+' : '−';
  return key === 'layout' || key === 'tasks' ? `${sign}${text}%` : `${sign}${text}`;
}

interface WeekMiniEntry {
  key: SectionKey;
  labelKey: string;
  /** Ranking light (green|amber|red only — AllBranchesReport contract).
   *  Muted entries report 'green' so they never rank as problems. */
  light: Light;
  /** Low data: grey dot, no delta, never counts as «all clear». */
  muted: boolean;
  frac: number;
  /** Formatted this_week value in the section's unit; null = no data at all. */
  valueText: string | null;
  /** Formatted absolute week-over-week delta; only when prev is comparable. */
  deltaText: string | null;
  /** The section's single worst row («Выпечка −18%»), only when it worsened. */
  worstRow: { label: string; deltaText: string } | null;
}

function toEntry(key: SectionKey, s: WeeklySection): WeekMiniEntry | null {
  // Unconfigured sections say nothing. Two fully-empty weeks are
  // indistinguishable from unconfigured (e.g. the tasks section never flips
  // its `configured` flag) — omit those too instead of nagging «мало данных».
  if (s.configured === false) return null;
  if (s.this_week == null && s.prev_week == null) return null;

  const base = {
    key,
    labelKey: SECTION_LABEL_KEY[key],
    light: 'green' as Light,
    // Muted entries sink below even improved-green ones (negative fracs).
    frac: Number.NEGATIVE_INFINITY,
    deltaText: null,
    worstRow: null,
  };

  // Data existed last week but not this one — honest grey line, no numbers.
  if (s.this_week == null) return { ...base, muted: true, valueText: null };

  const valueText = fmtValue(key, s.this_week);
  const comparable =
    s.delta_pct != null && s.prev_week != null && Math.abs(s.prev_week) >= PREV_FLOOR[key];
  // Current value is real but the base is missing/too thin to compare against.
  if (!comparable) {
    // TABLES: don't fall to grey «сравнить не с чем» — a clean→dirty branch has
    // no comparable baseline yet still deserves a colour. Read ABSOLUTE severity
    // off this week's dirty-alert rate; the entry stays a real (non-muted)
    // signal so it colours the dot/ring and ranks like the delta-based ones
    // (797/798, Alexey OK via coordinator 2026-07-22). Other sections keep the
    // grey no-baseline line (their «−100%» garbage guard is unchanged).
    if (key === 'tables') {
      const light = absTablesLight(s.this_week);
      return {
        ...base,
        muted: false,
        light,
        // amber/red rank among real problems; clean-green keeps base's -Inf so
        // it never outranks an actual signal.
        frac: light === 'red' ? RED_FRAC : light === 'amber' ? AMBER_FRAC : base.frac,
        valueText,
      };
    }
    // Other sections: grey dot + value, no delta (kills the «−100%» garbage).
    return { ...base, muted: true, valueText };
  }

  const frac = worseFrac(s.delta_pct!, s.prev_week!, s.higher_is_better);
  const light = lightOf(frac);

  // Muted sub-line: the row that dragged the section down. Only on worsening
  // sections (keeps healthy cards one-line-per-section small), and only rows
  // whose own base clears the same comparison floor.
  let worstRow: WeekMiniEntry['worstRow'] = null;
  if (light !== 'green' && s.rows?.length) {
    let bestFrac = 0;
    for (const r of s.rows) {
      if (r.delta_pct == null || r.prev_week == null) continue;
      if (Math.abs(r.prev_week) < PREV_FLOOR[key]) continue;
      const rf = worseFrac(r.delta_pct, r.prev_week, s.higher_is_better);
      const text = fmtDelta(key, r.delta_pct);
      if (rf > bestFrac && text != null) {
        bestFrac = rf;
        worstRow = { label: r.label, deltaText: text };
      }
    }
  }

  return {
    ...base,
    muted: false,
    light,
    frac,
    valueText,
    deltaText: fmtDelta(key, s.delta_pct!),
    worstRow,
  };
}

export function weekMiniEntries(
  week: Partial<WeeklyReportSections> | null | undefined,
): WeekMiniEntry[] {
  if (!week) return [];
  const entries = SECTION_ORDER.flatMap((key) => {
    const s = week[key];
    const e = s ? toEntry(key, s) : null;
    return e ? [e] : [];
  });
  // Worst first, muted last; ties keep the canonical order (flatMap is stable).
  return entries.sort((a, b) => b.frac - a.frac);
}

export function ZoneWeekMiniCard({
  zone,
  onOpen,
}: {
  zone: PortfolioZoneReportDto;
  onOpen: (zoneId: string) => void;
}) {
  const { t } = useT();
  const id = String(zone.zone_id);
  const entries = zone.report_exists ? weekMiniEntries(zone.week) : [];
  const hasFacts = entries.length > 0;
  // Ring by the worst REAL signal; an all-muted card stays neutral.
  const worst = entries.find((e) => !e.muted)?.light ?? null;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => { if (!document.getSelection()?.toString()) onOpen(id); }}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(id)}
      className={cn(
        'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        worst && `ring-1 ${LIGHT_RING[worst]}`,
      )}
    >
      <CardContent className="space-y-2.5 p-4">
        <h3 className="truncate font-medium">{zone.zone_name}</h3>

        {!hasFacts ? (
          <div className="text-sm text-muted-foreground">{t('portfolio.noWeekReport')}</div>
        ) : (
          // All-green cards show the same fact lines — the facts ARE the value
          // (a bare «Все в порядке» told the owner nothing).
          <ul className="space-y-1.5">
            {entries.map((e) => (
              <li key={e.key} className="text-sm">
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'inline-block h-2.5 w-2.5 shrink-0 rounded-full',
                      e.muted ? MUTED_DOT : LIGHT_DOT[e.light],
                    )}
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate',
                      e.muted ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {e.valueText != null ? (
                      t(FACT_KEY[e.key], { v: e.valueText })
                    ) : (
                      <>
                        {t(e.labelKey)}
                        <span className="text-muted-foreground">
                          {' · '}
                          {t('portfolio.weekLowData')}
                        </span>
                      </>
                    )}
                  </span>
                  {e.deltaText ? (
                    <span
                      className={cn(
                        'shrink-0 text-[11px] font-medium tabular-nums',
                        LIGHT_TEXT[e.light],
                      )}
                    >
                      {t('portfolio.weekDeltaChip', { delta: e.deltaText })}
                    </span>
                  ) : e.muted && e.valueText != null ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {t('portfolio.weekNoBaseline')}
                    </span>
                  ) : null}
                </div>
                {e.worstRow && (
                  <div className="truncate pl-4 text-[11px] text-muted-foreground">
                    {t('portfolio.weekWorst', {
                      label: e.worstRow.label,
                      delta: e.worstRow.deltaText,
                    })}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
